/**
 * Platform Settings Tab Component
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
// import { Textarea } from '../../ui/textarea';
// AlertDialog and Select imports kept for future merge feature
// import { AlertDialog, ... } from '../../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Menu, Building2, FolderTree,
  LayoutDashboard, TrendingUp, Calendar, ShoppingCart,
  ClipboardList, CheckCircle, Users, History as HistoryIcon,
  Newspaper, Factory, FileText, Upload, Layers,
  PackageCheck, ChevronDown, ChevronRight,
  FileQuestion, DollarSign, FileBox, ListOrdered, BarChart3, Truck, Package,
  AlertTriangle, FileCheck, ClipboardCheck, CalendarClock, PieChart,
  Loader2, Plus, Pencil, Trash2, Tag
} from 'lucide-react';
import { toast } from 'sonner';
// import { platformSettingsAPI } from '../../../lib/api';
import { PurchasingTabsVisibility, QualityTabsVisibility, purchasingAPI, CommodityGroup, CommodityType } from '../../../lib/api';
import { PlatformSettings, PORTAL_TYPES } from './types';

// Purchasing sub-tab registry for display
const PURCHASING_SUB_TABS = [
  { id: 'rfq', label: 'RFQ', description: 'Request for Quotation management', icon: FileQuestion },
  { id: 'pricing', label: 'Pricebook', description: 'Contract pricing and price books', icon: DollarSign },
  { id: 'po_headers', label: 'PO Headers', description: 'Purchase order header listing', icon: FileBox },
  { id: 'po_lines', label: 'PO Lines', description: 'Purchase order line items', icon: ListOrdered },
  { id: 'forecast', label: 'Forecast', description: 'Demand forecasting and planning', icon: BarChart3 },
  { id: 'asn', label: 'ASN', description: 'Advanced Shipping Notices', icon: Truck },
  { id: 'deliveries', label: 'Deliveries', description: 'Goods Receipt Notes and delivery tracking', icon: Package },
] as const;

// Quality sub-tab registry for display
const QUALITY_SUB_TABS = [
  { id: 'ncrs', label: 'NCRs', description: 'Non-Conformance Reports', icon: AlertTriangle },
  { id: 'concessions', label: 'Production Permit / Concession', description: 'Production permits and concessions management', icon: FileCheck },
  { id: 'approval_register', label: 'Approval Register', description: 'Track and manage approvals', icon: ClipboardCheck },
] as const;

interface PlatformTabProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
  purchasingTabs: PurchasingTabsVisibility;
  onPurchasingTabsChange: (tabs: PurchasingTabsVisibility) => void;
  onSavePurchasingTabs: () => void;
  qualityTabs: QualityTabsVisibility;
  onQualityTabsChange: (tabs: QualityTabsVisibility) => void;
  onSaveQualityTabs: () => void;
}

export function PlatformTab({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
  purchasingTabs,
  onPurchasingTabsChange,
  onSavePurchasingTabs,
  qualityTabs,
  onQualityTabsChange,
  onSaveQualityTabs,
}: PlatformTabProps) {
  // Merge Companies state — hidden until feature is production-ready
  // const [companies, setCompanies] = useState<Company[]>([]);
  // const [loadingCompanies, setLoadingCompanies] = useState(false);
  // const [targetCompanyId, setTargetCompanyId] = useState<string>('');
  // const [sourceCompanyId, setSourceCompanyId] = useState<string>('');
  // const [merging, setMerging] = useState(false);
  // const [mergeResult, setMergeResult] = useState<MergeCompaniesResponse | null>(null);

  // State for expanding sub-toggles
  const [purchasingExpanded, setPurchasingExpanded] = useState(false);
  const [qualityExpanded, setQualityExpanded] = useState(false);

  const updatePlatform = (updates: Partial<PlatformSettings>) => {
    onPlatformSettingsChange({ ...platformSettings, ...updates });
  };

  const updateMenuVisibility = (key: string, value: boolean) => {
    onPlatformSettingsChange({
      ...platformSettings,
      menuVisibility: { ...platformSettings.menuVisibility, [key]: value }
    });
  };

  const updatePurchasingTab = (key: string, value: boolean) => {
    onPurchasingTabsChange({ ...purchasingTabs, [key]: value });
  };

  const updateQualityTab = (key: string, value: boolean) => {
    onQualityTabsChange({ ...qualityTabs, [key]: value });
  };

  // IP Detection handler — hidden until feature is production-ready

  // Load companies for merge
  // Merge companies handlers — hidden until feature is production-ready
  // const handleLoadCompanies = async () => { ... };
  // const handleMergeCompanies = async () => { ... };

  const customerMenuCount = [
    platformSettings.menuVisibility.dashboard,
    platformSettings.menuVisibility.supplierPerformance,
    platformSettings.menuVisibility.analytics,
    platformSettings.menuVisibility.calendar,
    platformSettings.menuVisibility.partsMaster,
    platformSettings.menuVisibility.partSchedule,
    platformSettings.menuVisibility.purchasing,
    platformSettings.menuVisibility.callOff,
    platformSettings.menuVisibility.quality,
    platformSettings.menuVisibility.suppliers,
    platformSettings.menuVisibility.documents,
    platformSettings.menuVisibility.userInvitations,
    platformSettings.menuVisibility.userActivityHistory,
    platformSettings.menuVisibility.questionnaire,
    platformSettings.menuVisibility.newsRelease,
    platformSettings.menuVisibility.dataImport,
  ].filter(v => v).length;

  const supplierMenuItems = [
    platformSettings.menuVisibility.supplierDashboard,
    platformSettings.menuVisibility.supplierQuestionnaires,
    platformSettings.menuVisibility.supplierCalendar,
    platformSettings.menuVisibility.supplierCustomers,
    platformSettings.menuVisibility.supplierDocuments,
    platformSettings.menuVisibility.supplierNewsRelease,
    platformSettings.menuVisibility.purchaseOrders,
    platformSettings.menuVisibility.companyProfile,
    platformSettings.menuVisibility.callOff,
  ];
  const supplierMenuCount = supplierMenuItems.filter(v => v !== false).length;
  const supplierMenuTotal = supplierMenuItems.length;

  return (
    <div className="space-y-4">
      {/* Platform Configuration — moved to top per CR-4395 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Menu className="h-5 w-5" />
            Platform Configuration
          </CardTitle>
          <CardDescription>
            Select the portal type and menu structure
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="menuType">Portal Type</Label>
            <select
              id="menuType"
              value={platformSettings.mainMenuType}
              onChange={(e) => updatePlatform({ mainMenuType: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              {PORTAL_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">
              This determines the main menu structure and terminology used throughout the portal
            </p>
          </div>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Platform Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Menu Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Menu className="h-5 w-5" />
            Navigation Menu Management
          </CardTitle>
          <CardDescription>
            Control which menu items are visible in the side navigation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs defaultValue="customer" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="customer">Customer's View</TabsTrigger>
              <TabsTrigger value="supplier">Supplier's View</TabsTrigger>
            </TabsList>

            <TabsContent value="customer" className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b">
                <h3 className="font-semibold text-base">Customer's View Menu Items</h3>
                <Badge variant="secondary">{customerMenuCount} / 16 Enabled</Badge>
              </div>

              <div className="grid gap-3">
                <MenuToggleItem icon={<LayoutDashboard className="h-4 w-4 text-primary" />} label="Dashboard" description="Main dashboard overview" checked={platformSettings.menuVisibility.dashboard} onChange={(checked) => updateMenuVisibility('dashboard', checked)} />
                <MenuToggleItem icon={<TrendingUp className="h-4 w-4 text-primary" />} label="Supplier Performance" description="View supplier metrics and KPIs" checked={platformSettings.menuVisibility.supplierPerformance} onChange={(checked) => updateMenuVisibility('supplierPerformance', checked)} />
                <MenuToggleItem icon={<PieChart className="h-4 w-4 text-primary" />} label="Analytics" description="Reports, dashboards, and data analytics" checked={platformSettings.menuVisibility.analytics !== false} onChange={(checked) => updateMenuVisibility('analytics', checked)} />
                <MenuToggleItem icon={<Calendar className="h-4 w-4 text-primary" />} label="Calendar" description="Schedule and manage events" checked={platformSettings.menuVisibility.calendar} onChange={(checked) => updateMenuVisibility('calendar', checked)} />
                <MenuToggleItem icon={<Package className="h-4 w-4 text-primary" />} label="Parts Master" description="Manage parts and SKUs" checked={platformSettings.menuVisibility.partsMaster} onChange={(checked) => updateMenuVisibility('partsMaster', checked)} />
                <MenuToggleItem icon={<CalendarClock className="h-4 w-4 text-primary" />} label="Part Schedule" description="Part-level firm-boundary module (isolated)" checked={platformSettings.menuVisibility.partSchedule} onChange={(checked) => updateMenuVisibility('partSchedule', checked)} />
                {/* Purchasing with expandable sub-toggles */}
                <ExpandableMenuToggle
                  icon={<ShoppingCart className="h-4 w-4 text-primary" />}
                  label="Purchasing"
                  description="Manage purchase orders"
                  checked={platformSettings.menuVisibility.purchasing}
                  onChange={(checked) => { updateMenuVisibility('purchasing', checked); if (!checked) setPurchasingExpanded(false); }}
                  expanded={purchasingExpanded}
                  onExpandToggle={() => setPurchasingExpanded(!purchasingExpanded)}
                  subTabs={PURCHASING_SUB_TABS}
                  subTabValues={purchasingTabs}
                  onSubTabChange={updatePurchasingTab}
                  onSave={onSavePurchasingTabs}
                  saving={saving}
                />
                <MenuToggleItem icon={<PackageCheck className="h-4 w-4 text-primary" />} label="Call Off" description="Stock planning and call-off management" checked={platformSettings.menuVisibility.callOff} onChange={(checked) => updateMenuVisibility('callOff', checked)} />

                {/* Quality with expandable sub-toggles */}
                <ExpandableMenuToggle
                  icon={<CheckCircle className="h-4 w-4 text-primary" />}
                  label="Quality"
                  description="Quality management and NCRs"
                  checked={platformSettings.menuVisibility.quality}
                  onChange={(checked) => { updateMenuVisibility('quality', checked); if (!checked) setQualityExpanded(false); }}
                  expanded={qualityExpanded}
                  onExpandToggle={() => setQualityExpanded(!qualityExpanded)}
                  subTabs={QUALITY_SUB_TABS}
                  subTabValues={qualityTabs}
                  onSubTabChange={updateQualityTab}
                  onSave={onSaveQualityTabs}
                  saving={saving}
                />

                <MenuToggleItem icon={<Users className="h-4 w-4 text-primary" />} label="Suppliers" description="Manage supplier database" checked={platformSettings.menuVisibility.suppliers} onChange={(checked) => updateMenuVisibility('suppliers', checked)} />
                <MenuToggleItem icon={<FileText className="h-4 w-4 text-primary" />} label="Documents" description="Document management" checked={platformSettings.menuVisibility.documents} onChange={(checked) => updateMenuVisibility('documents', checked)} />
                <MenuToggleItem icon={<Users className="h-4 w-4 text-primary" />} label="User Invitations" description="Invite and manage users" checked={platformSettings.menuVisibility.userInvitations} onChange={(checked) => updateMenuVisibility('userInvitations', checked)} />
                <MenuToggleItem icon={<HistoryIcon className="h-4 w-4 text-primary" />} label="User Activity History" description="Track user actions and logs" checked={platformSettings.menuVisibility.userActivityHistory} onChange={(checked) => updateMenuVisibility('userActivityHistory', checked)} />
                <MenuToggleItem icon={<ClipboardList className="h-4 w-4 text-primary" />} label="Questionnaire" description="Supplier questionnaires" checked={platformSettings.menuVisibility.questionnaire} onChange={(checked) => updateMenuVisibility('questionnaire', checked)} />
                <MenuToggleItem icon={<Newspaper className="h-4 w-4 text-primary" />} label="News Release" description="Latest news and updates" checked={platformSettings.menuVisibility.newsRelease} onChange={(checked) => updateMenuVisibility('newsRelease', checked)} />
                <MenuToggleItem icon={<Upload className="h-4 w-4 text-primary" />} label="Data Import" description="Import data from external systems" checked={platformSettings.menuVisibility.dataImport} onChange={(checked) => updateMenuVisibility('dataImport', checked)} />
              </div>

              <Button onClick={onSave} disabled={saving} className="w-full mt-4">
                {saving ? 'Saving...' : 'Save Menu Visibility Settings'}
              </Button>
            </TabsContent>

            <TabsContent value="supplier" className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b">
                <h3 className="font-semibold text-base">Supplier's View Menu Items</h3>
                <Badge variant="secondary">{supplierMenuCount} / {supplierMenuTotal} Enabled</Badge>
              </div>

              <div className="grid gap-3">
                <MenuToggleItem icon={<LayoutDashboard className="h-4 w-4 text-primary" />} label="Dashboard" description="Supplier-side dashboard. Hide for compliance-only tenants that only need onboarding + questionnaires." checked={platformSettings.menuVisibility.supplierDashboard !== false} onChange={(checked) => updateMenuVisibility('supplierDashboard', checked)} />
                <MenuToggleItem icon={<ClipboardList className="h-4 w-4 text-primary" />} label="Questionnaires" description="List of qualification questionnaires assigned to the supplier." checked={platformSettings.menuVisibility.supplierQuestionnaires !== false} onChange={(checked) => updateMenuVisibility('supplierQuestionnaires', checked)} />
                <MenuToggleItem icon={<Calendar className="h-4 w-4 text-primary" />} label="Calendar" description="Calendar / events feed. Hide if not in use." checked={platformSettings.menuVisibility.supplierCalendar !== false} onChange={(checked) => updateMenuVisibility('supplierCalendar', checked)} />
                <MenuToggleItem icon={<Users className="h-4 w-4 text-primary" />} label="Customers" description="List of customers the supplier trades with. Hide when the supplier only deals with one buyer." checked={platformSettings.menuVisibility.supplierCustomers !== false} onChange={(checked) => updateMenuVisibility('supplierCustomers', checked)} />
                <MenuToggleItem icon={<FileText className="h-4 w-4 text-primary" />} label="Documents" description="Shared + uploaded document library." checked={platformSettings.menuVisibility.supplierDocuments !== false} onChange={(checked) => updateMenuVisibility('supplierDocuments', checked)} />
                <MenuToggleItem icon={<Newspaper className="h-4 w-4 text-primary" />} label="News Release" description="Customer-published news announcements." checked={platformSettings.menuVisibility.supplierNewsRelease !== false} onChange={(checked) => updateMenuVisibility('supplierNewsRelease', checked)} />
                <MenuToggleItem icon={<ShoppingCart className="h-4 w-4 text-primary" />} label="Purchase Orders" description="View and manage POs" checked={platformSettings.menuVisibility.purchaseOrders} onChange={(checked) => updateMenuVisibility('purchaseOrders', checked)} />
                <MenuToggleItem icon={<Factory className="h-4 w-4 text-primary" />} label="Company Profile" description="Manage company information" checked={platformSettings.menuVisibility.companyProfile} onChange={(checked) => updateMenuVisibility('companyProfile', checked)} />
                <MenuToggleItem icon={<PackageCheck className="h-4 w-4 text-primary" />} label="Call Off" description="Call-off management and stock upload" checked={platformSettings.menuVisibility.callOff} onChange={(checked) => updateMenuVisibility('callOff', checked)} />
              </div>

              <Button onClick={onSave} disabled={saving} className="w-full mt-4">
                {saving ? 'Saving...' : 'Save Menu Visibility Settings'}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Supplier Default Landing Page */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5" />
            Supplier Default Landing Page
          </CardTitle>
          <CardDescription>
            Where approved supplier users land after login. Pre-approval suppliers are always sent to My Onboarding.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier-landing" className="text-base font-medium">Landing page</Label>
            <Select
              value={platformSettings.supplierDefaultLandingPath}
              onValueChange={(value) => updatePlatform({ supplierDefaultLandingPath: value })}
            >
              <SelectTrigger id="supplier-landing" className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="/dashboard">Dashboard</SelectItem>
                <SelectItem value="/onboarding/my-progress">My Onboarding</SelectItem>
                <SelectItem value="/purchasing">Sales Orders</SelectItem>
                <SelectItem value="/suppliers">Company Profile</SelectItem>
                <SelectItem value="/intelligence/qualifications">Questionnaires</SelectItem>
                <SelectItem value="/customers">Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Landing Page'}
          </Button>
        </CardContent>
      </Card>

      {/* Main Company Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Supplier/Customer Main Company
          </CardTitle>
          <CardDescription>
            Configure main company settings and permissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label className="text-base font-medium">Main Company Can Log In As</Label>
              <p className="text-sm text-muted-foreground">
                Allow main company users to impersonate sub-accounts
              </p>
            </div>
            <Switch
              checked={platformSettings.mainCompanyCanLoginAs}
              onCheckedChange={(checked) => updatePlatform({ mainCompanyCanLoginAs: checked })}
            />
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Hierarchy Labels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            Hierarchy Labels
          </CardTitle>
          <CardDescription>
            Customize the names used for each level of your organization hierarchy.
            These labels appear throughout the platform — org chart, view switcher, dashboard, and exports.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="level1Label" className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-purple-600" />
                Level 1 (Top)
              </Label>
              <Input
                id="level1Label"
                value={platformSettings.hierarchyLevel1Label}
                onChange={(e) => updatePlatform({ hierarchyLevel1Label: e.target.value })}
                placeholder="Group"
              />
              <p className="text-xs text-muted-foreground">e.g. Group, Region, Corporate</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="level2Label" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                Level 2 (Mid)
              </Label>
              <Input
                id="level2Label"
                value={platformSettings.hierarchyLevel2Label}
                onChange={(e) => updatePlatform({ hierarchyLevel2Label: e.target.value })}
                placeholder="Division"
              />
              <p className="text-xs text-muted-foreground">e.g. Division, Business Unit, Area</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="level3Label" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                Level 3 (Operational)
              </Label>
              <Input
                id="level3Label"
                value={platformSettings.hierarchyLevel3Label}
                onChange={(e) => updatePlatform({ hierarchyLevel3Label: e.target.value })}
                placeholder="Company"
              />
              <p className="text-xs text-muted-foreground">e.g. Company, Plant, Factory, Location</p>
            </div>
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Hierarchy Labels'}
          </Button>
        </CardContent>
      </Card>

      {/* Free Mode - Hidden until feature is production-ready */}
      {/* IP Restriction - Hidden until feature is production-ready */}
      {/* Merge Companies - Hidden until feature is production-ready
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Merge Companies
          </CardTitle>
          <CardDescription>
            Merge duplicate company records, transferring all data to the target company
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Target Company (Keep)</Label>
              <Select
                value={targetCompanyId}
                onValueChange={setTargetCompanyId}
                onOpenChange={(open) => { if (open) handleLoadCompanies(); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingCompanies ? 'Loading...' : 'Select target company'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} ({c.company_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This company will receive all data from the source
              </p>
            </div>

            <div className="space-y-2">
              <Label>Source Company (Delete)</Label>
              <Select
                value={sourceCompanyId}
                onValueChange={setSourceCompanyId}
                onOpenChange={(open) => { if (open) handleLoadCompanies(); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingCompanies ? 'Loading...' : 'Select source company'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.filter(c => String(c.id) !== targetCompanyId).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} ({c.company_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-destructive">
                This company will be deleted after merge
              </p>
            </div>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                disabled={!targetCompanyId || !sourceCompanyId || merging}
              >
                {merging ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging...</>
                ) : (
                  'Execute Merge'
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Company Merge</AlertDialogTitle>
                <AlertDialogDescription>
                  This action is irreversible. All data from the source company will be
                  transferred to the target company, and the source company will be permanently deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleMergeCompanies}>
                  Confirm Merge
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {mergeResult && (
            <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <p className="font-semibold text-green-600">{mergeResult.message}</p>
              <p className="text-sm">
                Total records transferred: {mergeResult.total_records_transferred}
              </p>
              {mergeResult.transfers.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">Transfer Details:</p>
                  <div className="text-xs space-y-1">
                    {mergeResult.transfers.map((t, i) => (
                      <div key={i} className="flex justify-between bg-background p-2 rounded">
                        <span>{t.model}.{t.field}</span>
                        <Badge variant="secondary">{t.records_transferred} records</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      */}

      {/* User Notification Defaults moved to its own tab */}

      {/* Manage Commodity Groups and Types - Moved from Purchasing tab */}
      <CommodityGroupsSection />
    </div>
  );
}

// Helper component for menu toggle items
interface MenuToggleItemProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function MenuToggleItem({ icon, label, description, checked, onChange }: MenuToggleItemProps) {
  return (
    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded">
          {icon}
        </div>
        <div>
          <Label className="text-sm font-medium">{label}</Label>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

// Reusable expandable menu toggle with sub-tabs
interface SubTab {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface ExpandableMenuToggleProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  expanded: boolean;
  onExpandToggle: () => void;
  subTabs: readonly SubTab[];
  subTabValues: Record<string, boolean>;
  onSubTabChange: (id: string, value: boolean) => void;
  onSave: () => void;
  saving: boolean;
}

function ExpandableMenuToggle({
  icon, label, description, checked, onChange,
  expanded, onExpandToggle, subTabs, subTabValues,
  onSubTabChange, onSave, saving
}: ExpandableMenuToggleProps) {
  const enabledCount = subTabs.filter(tab => subTabValues[tab.id]).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded">{icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">{label}</Label>
              {checked && (
                <Badge variant="outline" className="text-xs">
                  {enabledCount} / {subTabs.length} tabs
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          {checked && (
            <button
              type="button"
              onClick={onExpandToggle}
              className="p-1 hover:bg-accent rounded mr-2"
              title={expanded ? 'Collapse sub-tabs' : 'Expand sub-tabs'}
            >
              {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </button>
          )}
        </div>
        <Switch checked={checked} onCheckedChange={onChange} />
      </div>
      {checked && expanded && (
        <div className="ml-6 pl-4 border-l-2 border-muted space-y-2">
          <p className="text-xs text-muted-foreground mb-2">Control which tabs are visible within {label}</p>
          {subTabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <div key={tab.id} className="flex items-center justify-between p-2 border rounded-lg hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-2">
                  <IconComponent className="h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <Label className="text-xs font-medium">{tab.label}</Label>
                    <p className="text-xs text-muted-foreground">{tab.description}</p>
                  </div>
                </div>
                <Switch checked={subTabValues[tab.id]} onCheckedChange={(v) => onSubTabChange(tab.id, v)} />
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={onSave} disabled={saving} className="w-full mt-2">
            {saving ? 'Saving...' : 'Save Tab Visibility'}
          </Button>
        </div>
      )}
    </div>
  );
}

// Commodity Groups Section - Moved from Purchasing tab
function CommodityGroupsSection() {
  const [groups, setGroups] = useState<CommodityGroup[]>([]);
  const [types, setTypes] = useState<CommodityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CommodityGroup | null>(null);
  const [editingType, setEditingType] = useState<CommodityType | null>(null);
  const [saving, setSaving] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', enabled: true });
  const [typeForm, setTypeForm] = useState({ name: '', commodity_group: 0, enabled: true });
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'group' | 'type'; item: CommodityGroup | CommodityType } | null>(null);
  const [preSelectedGroupId, setPreSelectedGroupId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsRes, typesRes] = await Promise.all([
        purchasingAPI.getCommodityGroups({ page_size: 100 }),
        purchasingAPI.getCommodityTypes({ page_size: 500 }),
      ]);
      const groupsData = groupsRes.data;
      const typesData = typesRes.data;
      setGroups(Array.isArray(groupsData) ? groupsData : (groupsData as Record<string, unknown>)['results'] as CommodityGroup[]);
      setTypes(Array.isArray(typesData) ? typesData : (typesData as Record<string, unknown>)['results'] as CommodityType[]);
    } catch (err: unknown) {
      console.error('Error loading commodities:', err);
      toast.error('Failed to load commodity data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (groupId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const openGroupDialog = (group?: CommodityGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({ name: group.name, enabled: group.enabled });
    } else {
      setEditingGroup(null);
      setGroupForm({ name: '', enabled: true });
    }
    setGroupDialogOpen(true);
  };

  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error('Group name is required'); return; }
    setSaving(true);
    try {
      if (editingGroup) {
        await purchasingAPI.updateCommodityGroup(editingGroup.id, groupForm);
        toast.success('Commodity group updated');
      } else {
        await purchasingAPI.createCommodityGroup(groupForm);
        toast.success('Commodity group created');
      }
      setGroupDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Error saving group:', err);
      toast.error('Failed to save commodity group');
    } finally { setSaving(false); }
  };

  const deleteGroup = async (group: CommodityGroup) => {
    if (group.type_count > 0) {
      toast.error(`Cannot delete "${group.name}" — it has ${group.type_count} commodity type(s). Remove them first.`);
      return;
    }
    setDeleteTarget({ type: 'group', item: group });
  };

  const openTypeDialog = (groupId?: number, type?: CommodityType) => {
    if (type) {
      setEditingType(type);
      setTypeForm({ name: type.name, commodity_group: type.commodity_group, enabled: type.enabled });
      setPreSelectedGroupId(null);
    } else {
      setEditingType(null);
      setTypeForm({ name: '', commodity_group: groupId || 0, enabled: true });
      setPreSelectedGroupId(groupId || null);
    }
    setTypeDialogOpen(true);
  };

  const saveType = async () => {
    if (!typeForm.name.trim()) { toast.error('Type name is required'); return; }
    if (!typeForm.commodity_group) { toast.error('Please select a commodity group'); return; }
    setSaving(true);
    try {
      if (editingType) {
        await purchasingAPI.updateCommodityType(editingType.id, typeForm);
        toast.success('Commodity type updated');
      } else {
        await purchasingAPI.createCommodityType(typeForm);
        toast.success('Commodity type created');
      }
      setTypeDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Error saving type:', err);
      toast.error('Failed to save commodity type');
    } finally { setSaving(false); }
  };

  const deleteType = async (type: CommodityType) => { setDeleteTarget({ type: 'type', item: type }); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'group') {
        await purchasingAPI.deleteCommodityGroup(deleteTarget.item.id);
        toast.success('Commodity group deleted');
      } else {
        await purchasingAPI.deleteCommodityType(deleteTarget.item.id);
        toast.success('Commodity type deleted');
      }
      setDeleteTarget(null);
      await fetchData();
    } catch (err: unknown) {
      console.error('Error deleting:', err);
      toast.error(`Failed to delete commodity ${deleteTarget.type}`);
    }
  };

  const toggleGroupEnabled = async (group: CommodityGroup) => {
    setGroups(prev => prev.map(g => g.id === group.id ? { ...g, enabled: !g.enabled } : g));
    try {
      await purchasingAPI.updateCommodityGroup(group.id, { enabled: !group.enabled });
    } catch (err: unknown) {
      console.error('Error toggling group:', err);
      toast.error('Failed to update commodity group');
      await fetchData();
    }
  };

  const toggleTypeEnabled = async (type: CommodityType) => {
    setTypes(prev => prev.map(t => t.id === type.id ? { ...t, enabled: !t.enabled } : t));
    try {
      await purchasingAPI.updateCommodityType(type.id, { enabled: !type.enabled });
    } catch (err: unknown) {
      console.error('Error toggling type:', err);
      toast.error('Failed to update commodity type');
      await fetchData();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Manage Commodity Groups and Types
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Commodity groups and types are used to categorize PO lines for filtering and reporting
              </p>
            </div>
            <div className="flex gap-2">              
              <Button size="sm" onClick={() => openGroupDialog()}>
                <Plus className="h-4 w-4 mr-1" />
                Add Group
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No commodity groups yet</p>
              <p className="text-sm mt-1">Create a commodity group to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32 text-center">Types</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-40 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => {
                  const isExpanded = expandedGroups.has(group.id);
                  const groupTypes = types.filter((t) => t.commodity_group === group.id);
                  return (
                    <>
                      <TableRow key={`group-${group.id}`} className="bg-muted/30">
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleExpand(group.id)}>
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 font-medium">
                            <Layers className="h-4 w-4 text-primary" />
                            {group.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-center"><Badge variant="secondary">{group.type_count}</Badge></TableCell>
                        <TableCell className="text-center">
                          <Switch checked={group.enabled} onCheckedChange={() => toggleGroupEnabled(group)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">                            
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openGroupDialog(group)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteGroup(group)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && groupTypes.map((type) => (
                        <TableRow key={`type-${type.id}`}>
                          <TableCell></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 pl-4">
                              <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                              {type.name}
                            </div>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-center">
                            <Switch checked={type.enabled} onCheckedChange={() => toggleTypeEnabled(type)} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openTypeDialog(undefined, type)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteType(type)}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {/* Empty type message */}
                    {isExpanded && groupTypes.length === 0 && (
                        <TableRow key={`empty-${group.id}`}>
                            <TableCell></TableCell>
                            <TableCell colSpan={4} className="text-sm text-muted-foreground pl-8">
                                No types in this group yet
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Add Type button row */}
                    {isExpanded && (
                        <TableRow key={`add-type-${group.id}`}>
                            <TableCell></TableCell>
                            <TableCell colSpan={4} className="pl-8">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openTypeDialog(group.id)}
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Add Type
                                </Button>
                            </TableCell>
                        </TableRow>
                    )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingGroup ? 'Edit Commodity Group' : 'New Commodity Group'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="e.g. Raw Materials" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={groupForm.enabled} onCheckedChange={(checked) => setGroupForm({ ...groupForm, enabled: checked })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveGroup} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingType ? 'Edit Commodity Type' : 'New Commodity Type'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Commodity Group</Label>
              <select
                value={typeForm.commodity_group}
                onChange={(e) => setTypeForm({ ...typeForm, commodity_group: parseInt(e.target.value) })}
                disabled={!!preSelectedGroupId}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value={0}>Select a group...</option>
                {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
              </select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} placeholder="e.g. Steel Fasteners" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={typeForm.enabled} onCheckedChange={(checked) => setTypeForm({ ...typeForm, enabled: checked })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveType} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Delete</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete {deleteTarget?.type === 'group' ? 'commodity group' : 'commodity type'}{' '}
            <strong>"{deleteTarget?.item.name}"</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
